import { vars } from '@styles/vanilla-extract/theme.css';
import {
	BoldVerticalBarChartData,
	BoldVerticalBarChartProps,
} from '@components/report/chart/bold-vertical-bar-chart/BoldVerticalBarChart';
import { formatBoldVerticalChartData } from '@components/report/chart/utils';
import { BlogPostContentItemProps } from '@components/report/content-items/blog-post-content-item/BlogPostContentItem';

import { NBContentCategoryStats, NBContentData, NBReportRes } from '@apis/orval/model';

import { roundFloat } from '@utils/round';

import { PostsProps, PostCategories, PostMetrics } from './Posts';

class PostsHashtagsAdapter {
	value: NBReportRes;

	constructor(value: NBReportRes) {
		this.value = value;
	}

	get hashtags(): string[] {
		const hashtagsArray = this.value.content?.main_hashtags ?? [];
		return hashtagsArray.filter(Boolean).slice(0, 5);
	}

	adapt(): string[] {
		return this.hashtags;
	}
}

class PostsCarouselAdapter {
	value: NBReportRes;

	constructor(value: NBReportRes) {
		this.value = value;
	}

	get posts() {
		const contents = this.value.content?.recent_contents ?? [];

		return contents.filter(Boolean);
	}

	adaptPostItem(postItem: NBContentData, idx: number): BlogPostContentItemProps {
		return {
			idx,
			imgSrc: postItem.content_img_link ?? undefined,
			categories: [postItem?.category]?.filter(Boolean) as string[],
			title: postItem.content_title ?? undefined,
			hashtags: postItem.hashtags ?? [],
		};
	}

	adapt(): BlogPostContentItemProps[] {
		return this.posts.map((post, idx) => this.adaptPostItem(post, idx));
	}
}

class PostCategoriesAdapter {
	value: NBReportRes;

	constructor(value: NBReportRes) {
		this.value = value;
	}

	get categoriesBase() {
		return this.value.content?.summary?.category_stats ?? [];
	}

	get largestCategory(): string {
		return this.value.content?.summary?.main_category ?? '';
	}
	get largestCategoryValue(): number {
		const largestCategoryRatio =
			this.categoriesBase?.find((data) => data.category === this.largestCategory)
				?.ratio ?? 0;

		return largestCategoryRatio * 100;
	}

	getCategoryDataItem(
		data: NBContentCategoryStats,
		largestCategory: string,
	): Exclude<PostCategories['topData'], undefined>[number] {
		const isLargest = data.category === largestCategory;
		const largestOption = {
			valueColor: vars.global.colors.primary[60],
			barColor: vars.global.colors.primary[60],
		};

		return {
			value: data?.ratio ? `${roundFloat(data.ratio * 100, 'down', 2)}%` : '-%',
			valuePercentage: data?.ratio ?? 0,
			...(isLargest ? largestOption : {}),
		};
	}

	get categoryData(): PostCategories['topData'] {
		return (
			this.categoriesBase
				?.map((data) => this.getCategoryDataItem(data, this.largestCategory))
				?.slice(0, 4) ?? []
		);
	}

	get categoryLabels(): PostCategories['categories'] {
		const labels = this.categoriesBase?.map((data) => data.category) ?? [];
		return (labels.filter(Boolean) as string[]).slice(0, 4);
	}

	get description() {
		if (this.largestCategory === '') return '게시글 카테고리가 존재하지 않습니다.';
		return (
			<>
				가장 많은 게시글 카테고리는 <span>{this.largestCategory}</span>입니다.
			</>
		);
	}

	adapt(): PostCategories {
		return {
			topData: this.categoryData,
			desc: this.description,
			largestCategoryValue: this.largestCategoryValue,
			categories: this.categoryLabels,
		};
	}
}

class PostMetricsAdapter {
	value: NBReportRes;

	constructor(value: NBReportRes) {
		this.value = value;
	}

	get postAvgViews() {
		return this.value.content?.summary?.avg_daily_view ?? undefined;
	}

	get postAvgImages() {
		return this.value.content?.summary?.avg_img_count ?? undefined;
	}

	get relatedLocation(): string[] {
		return this.value.content?.summary?.related_location ?? [];
	}

	adapt(): PostMetrics {
		return {
			averageViews: this.postAvgViews?.toLocaleString() ?? '-',
			averageImages: this.postAvgImages?.toLocaleString() ?? '-',
			relatedLocations: this.relatedLocation,
		};
	}
}

class AdPostCategoriesAdapter {
	value: NBReportRes;

	constructor(value: NBReportRes) {
		this.value = value;
	}

	get categoriesBase() {
		return this.value.content?.summary?.ad_content_summary?.top_category_stats ?? [];
	}

	get largestCategory(): string {
		return this.value.content?.summary?.ad_content_summary?.main_category ?? '';
	}
	get largestCategoryValue(): number {
		const largestCategoryRatio =
			this.categoriesBase?.find((data) => data.category === this.largestCategory)
				?.ratio ?? 0;

		return largestCategoryRatio * 100;
	}

	getCategoryDataItem(
		data: NBContentCategoryStats,
		largestCategory: string,
	): Exclude<PostCategories['topData'], undefined>[number] {
		const isLargest = data.category === largestCategory;
		const largestOption = {
			valueColor: vars.global.colors.primary[60],
			barColor: vars.global.colors.primary[60],
		};

		return {
			value: data?.ratio ? `${roundFloat(data.ratio * 100, 'down', 2)}%` : '-%',
			valuePercentage: data?.ratio ?? 0,
			...(isLargest ? largestOption : {}),
		};
	}

	get categoryData(): PostCategories['topData'] {
		return (
			this.categoriesBase
				?.map((data) => this.getCategoryDataItem(data, this.largestCategory))
				?.slice(0, 4) ?? []
		);
	}

	get categoryLabels(): PostCategories['categories'] {
		const labels = this.categoriesBase?.map((data) => data.category) ?? [];
		return (labels.filter(Boolean) as string[]).slice(0, 4);
	}

	get description() {
		if (this.largestCategory === '') return '광고 게시글 카테고리가 존재하지 않습니다.';
		return (
			<>
				가장 많은 광고 게시글 카테고리는 <span>{this.largestCategory}</span>입니다.
			</>
		);
	}

	adapt(): PostCategories {
		return {
			topData: this.categoryData,
			desc: this.description,
			largestCategoryValue: this.largestCategoryValue,
			categories: this.categoryLabels,
		};
	}
}

class AdPostRateAdapter {
	value: NBReportRes;

	constructor(obj: NBReportRes) {
		this.value = obj;
	}

	/**
	 * @description 광고 게시글 비율
	 */
	get adPostRate() {
		return this.value?.content?.summary?.ad_content_summary?.ad_content_rate ?? undefined;
	}
	/**
	 * @description 유사 그룹 광고 게시글 비율
	 */
	get similarGroupAdPostRate() {
		return (
			this.value?.content?.summary?.ad_content_summary?.similar_group_ad_content_rate ??
			undefined
		);
	}

	/**
	 * @description 비교 결과 텍스트
	 */
	textMapper = {
		// high: (
		// 	<Trans
		// 		// t={i18n?.t}
		// 		i18nKey={'infReportInstagram:engagement.engagement-rate.high-comparison'}
		// 		components={[<span key="high">{i18n?.t('infReportInstagram:engagement.engagement-rate.high')}</span>]}
		// 	/>
		// ),
		// normal: (
		// 	<Trans
		// 		// t={i18n?.t}
		// 		i18nKey={'infReportInstagram:engagement.engagement-rate.normal-comparison'}
		// 		components={[<span key="normal">{i18n?.t('infReportInstagram:engagement.engagement-rate.normal')}</span>]}
		// 	/>
		// ),
		// low: (
		// 	<Trans
		// 		// t={i18n?.t}
		// 		i18nKey={'infReportInstagram:engagement.engagement-rate.low-comparison'}
		// 		components={[<span key="low">{i18n?.t('infReportInstagram:engagement.engagement-rate.low')}</span>]}
		// 	/>
		// ),
		// unmeasurable: (
		// 	<Trans
		// 		// t={i18n?.t}
		// 		i18nKey={'infReportInstagram:engagement.engagement-rate.unmeasurable-comparison'}
		// 		components={[
		// 			<span key="unmeasurable">{i18n?.t('infReportInstagram:engagement.engagement-rate.unmeasurable')}</span>,
		// 		]}
		// 	/>
		// ),
		high: (
			<>
				보다 <span>높습니다.</span>
			</>
		),
		normal: (
			<>
				과 <span>비슷합니다.</span>
			</>
		),
		low: (
			<>
				보다 <span>낮습니다.</span>
			</>
		),
		unmeasurable: (
			<>
				과 <span>비교 예정입니다.</span>
			</>
		),
	} as const;

	/**
	 * @description 비교 결과 상태
	 */
	get status() {
		const hasAllValue = this.adPostRate && this.similarGroupAdPostRate;
		if (!hasAllValue) return 'unmeasurable';

		return this.adPostRate > this.similarGroupAdPostRate * 1.1
			? 'high'
			: this.adPostRate < this.similarGroupAdPostRate * 0.9
			? 'low'
			: 'normal';
	}

	/**
	 * @description 인플루언서 참여율 차트 데이터 (주 데이터)
	 */
	get infAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.adPostRate && this.similarGroupAdPostRate;

		return {
			value: `${roundFloat(this?.adPostRate ?? 0, 'down', 2)}%`,
			label: this.value?.account?.profile?.username ?? '',
			valuePercentage: hasAllValue
				? (this.adPostRate / (this.adPostRate + this.similarGroupAdPostRate)) * 100
				: undefined,
			colorType: this.status === 'high' ? 'primary' : 'black',
			isMain: true,
		} as const;
	}

	/**
	 * @description 유사그룹 평균 참여율 차트 데이터 (비교군 데이터)
	 */
	get similarGroupAdvancedData(): BoldVerticalBarChartData {
		const hasAllValue = this.adPostRate && this.similarGroupAdPostRate;

		return {
			value: `${roundFloat(this?.similarGroupAdPostRate ?? 0, 'down', 2)}%`,
			// label: i18n?.t('infReportInstagram:engagement.avg-real-engagement.similar-group'),
			label: '유사 그룹 평균',
			valuePercentage: hasAllValue
				? (this.similarGroupAdPostRate /
						(this.adPostRate + this.similarGroupAdPostRate)) *
				  100
				: undefined,
			colorType: 'gray',
		} as const;
	}

	/**
	 * @description 차트 데이터 전체
	 */
	get data(): BoldVerticalBarChartData[] {
		const realEngagementDefaultData = {
			label: this.value?.account?.profile?.username ?? '',
		};
		const avgRealEngagementDefaultData = {
			// label: i18n?.t('infReportInstagram:engagement.avg-real-engagement.similar-group'),
			label: '유사 그룹 평균',
		};

		const realEngagementData = formatBoldVerticalChartData(
			this.adPostRate,
			realEngagementDefaultData,
			this.infAdvancedData,
		);
		const avgRealEngagementData = formatBoldVerticalChartData(
			this.similarGroupAdPostRate,
			avgRealEngagementDefaultData,
			this.similarGroupAdvancedData,
		);

		return [realEngagementData, avgRealEngagementData];
	}

	/**
	 * @description 유효 팔로워 수 텍스트
	 */
	get valueText(): string {
		if (!this.adPostRate)
			// return i18n?.t('infReportInstagram:engagement.real-engagement.description.preparing') ?? '';
			return '데이터 준비 중으로';

		return (
			// i18n?.t('infReportInstagram:engagement.real-engagement.description.span-text', {
			// 	realEngagement: roundFloat(this.adPostRate, 'down', 2),
			// }) ?? ''
			`${roundFloat(this.adPostRate, 'down', 2)}%로`
		);
	}

	adapt(): BoldVerticalBarChartProps {
		return {
			desc: (
				// <Trans
				// 	// t={i18n?.t}
				// 	i18nKey={'infReportInstagram:engagement.real-engagement.description.sentence'}
				// 	components={[<span key="realEngagement">{this.valueText}</span>, this.textMapper[this.status]]}
				// />
				<>
					광고 게시글 비율이 <span>{this.valueText}</span>
					<br />
					유사 그룹 평균{this.textMapper[this.status]}
				</>
			),
			data: this.data,
		};
	}
}

export class PostsAdapter {
	value: NBReportRes;

	constructor(value: NBReportRes) {
		this.value = value;
	}

	adapt(): PostsProps {
		return {
			bannerHashtags: new PostsHashtagsAdapter(this.value).adapt(),
			postsCarousel: new PostsCarouselAdapter(this.value).adapt(),
			postCategories: new PostCategoriesAdapter(this.value).adapt(),
			postMetrics: new PostMetricsAdapter(this.value).adapt(),
			adPostCategories: new AdPostCategoriesAdapter(this.value).adapt(),
			adPostRate: new AdPostRateAdapter(this.value).adapt(),
		};
	}
}
